import { useMemo } from 'react';
import { Box } from '@mui/material';

import { Label, DataTable, Loading, ErrorMessage, type TableColumn } from '@/src/ui';
import type { ClassSummaryResultDTO } from '../models/classSummaryDTOs';
import {
    ReportCellStyle,
    ReportHeaderStyle,
    Constants,
    utcToLocal,
    formatOptions,
} from '@/src/utils';

interface ClassSummaryTableProps {
    data: ClassSummaryResultDTO[];
    isLoading: boolean;
    error: string;
    emptyMessage?: string;
}

const ClassSummaryTable: React.FC<ClassSummaryTableProps> = (props) => {
    const { data, isLoading, error, emptyMessage } = props;

    const columns = useMemo<TableColumn[]>(
        () => [
            {
                dataKey: 'studentName',
                header: 'Student Name',
                allowSort: true,
                align: 'left',
                emptyValue: '-',
                headerSx: ReportHeaderStyle,
                cellSx: ReportCellStyle,
            },
            {
                dataKey: 'attemptCount',
                header: 'Attempt Count',
                allowSort: true,
                align: 'center',
                emptyValue: '-',
                headerSx: ReportHeaderStyle,
                cellSx: ReportCellStyle,
            },
            {
                dataKey: 'completedDate',
                header: 'Completed Date',
                allowSort: true,
                formatter: (row: ClassSummaryResultDTO) => {
                    if (!row.completedDate) return '-';
                    return utcToLocal(row.completedDate).toLocaleString(undefined, formatOptions);
                },
                emptyValue: '-',
                headerSx: ReportHeaderStyle,
                cellSx: ReportCellStyle,
            },
            {
                dataKey: 'percentCorrect',
                header: 'Percent Correct',
                allowSort: true,
                align: 'center',
                formatter: (row: ClassSummaryResultDTO) =>
                    row.percentCorrect != null ? `${row.percentCorrect}%` : '-',
                emptyValue: '-',
                headerSx: ReportHeaderStyle,
                cellSx: ReportCellStyle,
            },
            {
                dataKey: 'scoringBand',
                header: 'Scoring Band',
                allowSort: true,
                align: 'center',
                emptyValue: '-',
                headerSx: ReportHeaderStyle,
                cellSx: ReportCellStyle,
                formatter: (row: ClassSummaryResultDTO) => {
                    const band = row.scoringBand;
                    if (!band) return '-';
                    return <Label color={Constants.bandColors[band]}>{band}</Label>;
                },
            },
        ],
        []
    );

    if (isLoading) {
        return <Loading message="Loading class summary..." />;
    }

    if (error) {
        return (
            <Box sx={{ textAlign: 'center', mb: 1 }}>
                <ErrorMessage error={error} />
            </Box>
        );
    }

    return (
        <div className="w-full overflow-auto">
            <br />
            <DataTable
                data={data.filter((row) => row.studentId && row.examId)}
                columns={columns}
                tableName="ClassSummaryTable"
                rowsPerPageInitial={50}
                emptyMessage={emptyMessage}
                enableExportCSV
                alternatingRowColor
                defaultSortColumn="studentName"
                defaultSortDirection="asc"
            />
        </div>
    );
};

export default ClassSummaryTable;
